import { useState, useEffect, useCallback } from 'react';
import { userProfileService } from '../services/userProfileService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Visnings-mapping fra UserID -> ProfileName (Profile-tabellen).
 *
 * KUN for presentasjon. UserID forblir nøkkelen i all lagring, filtrering og oppslag —
 * denne hooken endrer aldri hvilken verdi som sendes til backend, den bytter bare ut
 * hvilken tekst operatøren ser. Fallback er alltid selve UserID hvis navn mangler.
 *
 * Profillista lastes én gang per companyNo og caches på modulnivå slik at mange
 * komponenter (working-picking-lists, historikk, order-timeline, admin-tabeller ...)
 * deler samme oppslag uten å hamre endepunktet.
 */

type NameMap = Map<string, string>;

// Modul-cache per companyNo. Nullstilles implisitt ved full reload (innlogging/tenant-bytte).
const cacheByCompany = new Map<string, NameMap>();
const inflightByCompany = new Map<string, Promise<NameMap>>();

async function loadNameMap(companyNo: string): Promise<NameMap> {
  const cached = cacheByCompany.get(companyNo);
  if (cached) return cached;

  let inflight = inflightByCompany.get(companyNo);
  if (!inflight) {
    inflight = userProfileService
      .getAllProfilesForCompany()
      .then((profiles) => {
        const map: NameMap = new Map();
        for (const p of profiles) {
          if (p.userID) {
            map.set(p.userID.toLowerCase(), p.profileName || p.userID);
          }
        }
        cacheByCompany.set(companyNo, map);
        return map;
      })
      .finally(() => {
        inflightByCompany.delete(companyNo);
      });
    inflightByCompany.set(companyNo, inflight);
  }
  return inflight;
}

export interface UseOperatorDisplayNamesResult {
  /** UserID -> ProfileName, med fallback til UserID selv. Trygg med null/undefined. */
  getDisplayName: (userId?: string | null) => string;
  /** True når profillista er ferdig lastet. */
  loaded: boolean;
}

export function useOperatorDisplayNames(): UseOperatorDisplayNamesResult {
  const { user } = useAuth();
  const companyNo = user?.companyNo ?? '';
  const [map, setMap] = useState<NameMap | null>(() => cacheByCompany.get(companyNo) ?? null);

  useEffect(() => {
    if (!companyNo) return;
    let active = true;
    loadNameMap(companyNo)
      .then((m) => {
        if (active) setMap(m);
      })
      .catch(() => {
        // Stille feil: getDisplayName faller tilbake til UserID.
      });
    return () => {
      active = false;
    };
  }, [companyNo]);

  const getDisplayName = useCallback(
    (userId?: string | null): string => {
      if (!userId) return '';
      const active = map ?? cacheByCompany.get(companyNo) ?? null;
      return active?.get(userId.toLowerCase()) || userId;
    },
    [map, companyNo]
  );

  return { getDisplayName, loaded: !!map };
}
